import React, { useEffect, useContext, useState, useMemo } from 'react';
import { Form, Field, Formik } from 'formik';

import {
  isValidRegistrationNumber,
  isValidRegistrationNumberTrust,
  isValidUrl,
} from 'src/helpers/validation';
import { businessRegistrationMask, trustRegistrationMask } from 'src/helpers/formatting';
import { NATURE_OF_BUSINESS } from 'src/helpers/constants';
import TealiumEvents from 'src/helpers/tealium-events';
import {
  ecommerceProductCategoryName,
  LockedFieldNames,
  SalesforceContext,
  Steps,
  AcquiringChannelFlags,
} from 'src/context/salesforce-context';
import {
  Checkbox,
  FloatingInput,
  FloatingInputVersionTwo,
  FormLayout,
  Info,
  ShowIf,
} from 'src/components';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import { SOV_DETAILS } from 'src/helpers/constants';
import MiniAppEvents from 'src/helpers/miniapp-events';
import { BUSINESS_TYPE_KEY_TO_NAME_LOOKUP } from 'src/helpers/constants';
import { CONSUMER_TYPE } from 'src/services/salesforce.service';
import { StyledDatePicker } from 'src/components/styled-date-picker/styled-date-picker';
import { isProductCategorySelected, checkAcquiringChannel } from 'src/helpers/devices';
import { getFormStatus, merger } from 'src/features/onboarding/helpers/fields-status';
import mergeWith from 'lodash.mergewith';
import { Environment } from 'src/Environment';
import './your-business.scss';
import 'src/containers/onboarding/onboarding.scss';

export default function YourBusinessAddress(props) {
  const inMiniProgram = MiniAppEvents.isInMiniApp();
  const BUSINESS_REFERENCE_MAX_LENGTH = 23;
  const { merchant, setMerchant, lead, customerType, isFieldLocked, isVodacomBusinessChannel } =
    useContext(SalesforceContext);

  const isEcommerceSelected = isProductCategorySelected(
    ecommerceProductCategoryName,
    merchant?.opportunity?.deliveries,
  );

  const isRented = checkAcquiringChannel(
    AcquiringChannelFlags.rentOnlineFicaOnline,
    merchant?.opportunity?.deliveries,
  );

  const isCreditVettingEnabled = !Environment.current?.disabledFeatures?.creditVetting && isRented;

  const isTrust = lead.typeOfBusiness === BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.trust;

  const [inputDanger, setInputDanger] = useState({
    'business-name': false,
    'trading-as': false,
    'company-reg-number': false,
  });
  const [showCompanyRegNumber, setShowCompanyRegNumber] = useState(false);
  const [industryData] = useState(NATURE_OF_BUSINESS);
  const [selectedIndustry, setSelectedIndustry] = useState(null);

  useEffect(() => {
    if (merchant?.account?.business_type) {
      const assistantId = lead.dealerId || lead.agentId || '';
      TealiumEvents.yourBusinessPageLoadFormStartCombined(
        merchant.account.business_type,
        customerType,
        assistantId,
        merchant.isPendingApp,
      );
    }
  }, [merchant?.account?.business_type]);

  const creditCheckTooltipText =
    'A credit check helps us verify your bank account details to make sure that the ' +
    'registered account not only exists but also belongs to you so we can successfully credit or debit it.';
  const creditCheckRadioButtonText =
    'I consent to the processing of my personal information,\n' + 'including credit bureau checks.';
  const businessUrlInfoText =
    'Please provide us with the URL to your e-commerce website. We will need to verify your e-commerce platform in order to activate the payment gateway.';

  const textTable = {
    dealer: {
      title: "Tell us about the customer's business",
      subtitle: "Please provide the below details about the customer's business",
      toggle: 'The business is known by a different name',
      creditCheckConsent: creditCheckRadioButtonText,
    },
    agent: {
      title: "Tell us about the customer's business",
      subtitle: "Please provide the below details about the customer's business",
      toggle: 'The business is known by a different name',
      creditCheckConsent: creditCheckRadioButtonText,
    },
    normal: {
      title:
        lead.consumerType === CONSUMER_TYPE.owner
          ? 'Tell us about your business'
          : "Tell us about the customer's business",
      subtitle:
        lead.consumerType === CONSUMER_TYPE.owner
          ? 'Please provide the below details about your business'
          : "Please provide the below details about the customer's business",
      toggle:
        lead.consumerType === CONSUMER_TYPE.owner
          ? 'My business is known by a different name'
          : 'The business is known by a different name',
      creditCheckConsent: creditCheckRadioButtonText,
    },
    pos: {
      title: 'Tell us about your business',
      subtitle: 'Please provide the below details about your business',
      toggle: 'My business is known by a different name',
      creditCheckConsent: creditCheckRadioButtonText,
    },
  };

  const getIndustry = (industryName) => {
    return industryData.find((datum) => {
      return datum.broadCategory.toLowerCase() === industryName.toLowerCase();
    });
  };

  const getCategory = (industryName, categoryName) => {
    const industry = industryData.find((datum) => {
      return datum.broadCategory.toLowerCase() === industryName.toLowerCase();
    });

    if (industry) {
      return industry?.categories.find((category) => {
        return category.description.toLowerCase() === categoryName.toLowerCase();
      });
    }
  };

  const isNotSoleProp = (businessType) =>
    businessType !== BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.sole_prop;

  useEffect(() => {
    const industry = getIndustry(getBusinessDetails().industry);

    if (industry) {
      setSelectedIndustry(industry);
    }

    setShowCompanyRegNumber(isNotSoleProp(lead.typeOfBusiness));

    props.toggleNext(isFormValid(getBusinessDetails()));
  }, [merchant]);

  const getBusinessDetails = () => {
    const tradingNameDifferentToBusinessName =
      merchant?.account?.trading_name_different_to_business_name || false;
    return {
      businessName:
        merchant?.account?.business_name !== 'Company Placeholder'
          ? merchant?.account?.business_name || ''
          : '',
      tradingAs: tradingNameDifferentToBusinessName ? merchant?.account?.trading_as || '' : '',
      companyRegNumber: merchant?.account?.registration_number || '',
      industry: merchant?.account?.industry || '',
      natureOfBusiness: merchant?.account?.nature_of_business || '',
      tradingNameDifferentToBusinessName: tradingNameDifferentToBusinessName,
      occupation: merchant?.contact?.Occupation || '',
      sourceOfFunds: merchant?.contact?.Source_funds || '',
      sourceOfWealth: merchant?.contact?.Source_wealth || '',
      creditCheckConsent: merchant?.account?.business_credit_vetting_consent || false,
      businessRegistrationDate: merchant?.account?.company_date_of_registration || null,
      ecommerceUrl: merchant?.account?.business_website || '',
    };
  };

  const initialStatus = useMemo(() => {
    const defaultStatus = {
      initialDisabled: {
        tradingAs: isFieldLocked(LockedFieldNames.ACCOUNT_TRADING_AS),
      },
    };

    if (merchant.isPendingApp && !merchant?.pageUpdates) {
      throw new Error('Your business legacy: in pending app but no page updates available');
    }

    if (!merchant.isPendingApp) {
      return defaultStatus;
    }

    const allFields = getBusinessDetails();
    const pendingAppStatus = getFormStatus(merchant.pageUpdates, allFields, Steps.BUSINESS_DETAILS);

    if (isVodacomBusinessChannel) {
      return mergeWith(pendingAppStatus, defaultStatus, merger);
    }

    return pendingAppStatus;
  }, [merchant?.isPendingApp, merchant?.pageUpdates]);

  const isSovRequest = merchant?.account?.sov_requested;

  const setBusinessDetails = (details) => {
    const merchantNew = {
      ...merchant,
      account: {
        ...merchant.account,
        business_name: details.businessName,
        trading_as: details.tradingNameDifferentToBusinessName
          ? details.tradingAs
          : details.businessName,
        registration_number: details.companyRegNumber,
        industry: details.industry,
        nature_of_business: details.natureOfBusiness,
        mcc: getCategory(details.industry, details.natureOfBusiness)?.mcc,
        trading_name_different_to_business_name: details.tradingNameDifferentToBusinessName,
        business_website: details.ecommerceUrl,
      },
      contact: {
        ...merchant.contact,
        Occupation: details.occupation,
        Source_funds: details.sourceOfFunds,
        Source_wealth: details.sourceOfWealth,
      },
    };

    if (isCreditVettingEnabled) {
      merchantNew.account.company_date_of_registration = details.businessRegistrationDate;
      merchantNew.account.business_credit_vetting_consent = details.creditCheckConsent || false;
    }

    setMerchant(merchantNew);
  };

  return (
    <div className="YourBusiness" data-testid="YourBusiness">
      <div className="max-field-width">
        <div className="has-text-centered">
          <h1 className="color-whitegrey heading-four title-text">
            {textTable[customerType].title}
          </h1>
          <p className="content-normal page-subtitle mobile-width-limit subtext">
            {textTable[customerType].subtitle}
          </p>
        </div>
        <div className="business-details-container">
          <div className="business-details">
            <Formik
              initialStatus={initialStatus}
              initialValues={getBusinessDetails()}
              validate={validateForm}
              validateOnChange={true}
              validateOnBlur={true}
              enableReinitialize={true}
            >
              {({ values, setFieldValue }) => {
                return (
                  <Form>
                    <Field
                      name="businessName"
                      type="text"
                      placeholder="Business name"
                      maxLength={BUSINESS_REFERENCE_MAX_LENGTH}
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInput}
                      fieldType="standard"
                      className="dark top-field bottom-field"
                      automationid="Onboarding_Create_Account_Your_Business_Field_Business_Name"
                      autoFocus
                    />
                    <div className="checkbox-spacing">
                      <Field
                        name="tradingNameDifferentToBusinessName"
                        type="checkbox"
                        component={MiniAppFocusWrapper}
                        innerComponent={Checkbox}
                        checkboxText={textTable[customerType].toggle}
                        disabled={isFieldLocked(LockedFieldNames.ACCOUNT_TRADING_AS)}
                        automationid="Onboarding_Create_Account_Your_Business_Field_Trading_Toggle"
                      />
                    </div>
                    {values.tradingNameDifferentToBusinessName && (
                      <Field
                        name="tradingAs"
                        type="text"
                        placeholder="Trading as"
                        maxLength={BUSINESS_REFERENCE_MAX_LENGTH}
                        component={MiniAppFocusWrapper}
                        innerComponent={FloatingInput}
                        fieldType="standard"
                        className="dark top-field"
                        automationid="Onboarding_Create_Account_Your_Business_Field_Trading_As"
                      />
                    )}
                    {showCompanyRegNumber && (
                      <Field
                        name="companyRegNumber"
                        mask={isTrust ? trustRegistrationMask : businessRegistrationMask}
                        automationid="Onboarding_Create_Account_Your_Business_Field_Company_Registration_Number"
                        placeholder="Business registration number"
                        type="tel"
                        fieldType="masked"
                        guide={!inMiniProgram}
                        component={MiniAppFocusWrapper}
                        innerComponent={FloatingInput}
                        className="dark"
                      />
                    )}
                    <ShowIf condition={isCreditVettingEnabled}>
                      <div className="checkbox-spacing">
                        <Field
                          name="creditCheckConsent"
                          type="checkbox"
                          component={MiniAppFocusWrapper}
                          innerComponent={Checkbox}
                          checkboxText={() => (
                            <div className="credit-check-consent">
                              {textTable[customerType].creditCheckConsent}
                              <Info
                                className="checkbox-info"
                                text={creditCheckTooltipText}
                                orientation="top"
                                onOpenEvent={() => TealiumEvents.generalInfoClick(customerType)}
                              />
                            </div>
                          )}
                          automationid="Onboarding_Create_Account_Your_Business_Field_Credit_Consent"
                        />
                      </div>
                    </ShowIf>
                    <FormLayout>
                      <ShowIf
                        condition={isCreditVettingEnabled && isNotSoleProp(lead.typeOfBusiness)}
                      >
                        <Field
                          name="businessRegistrationDate"
                          fieldName="When did you register your business?"
                          component={MiniAppFocusWrapper}
                          innerComponent={StyledDatePicker}
                          minDate={new Date('01/01/1700')} //Sales Force only allows from the year 1700 and up.
                          maxDate={new Date()}
                        />
                      </ShowIf>
                      <Field
                        name="industry"
                        type="text"
                        placeholder="What industry are you in?"
                        component={MiniAppFocusWrapper}
                        innerComponent={FloatingInput}
                        items={industryData}
                        getItemDisplayValue={(item) => item.broadCategory}
                        filterItemsWithInputValue={true}
                        onItemSelect={(item) => {
                          setSelectedIndustry(item);
                          if (item && !getCategory(item.broadCategory, values?.natureOfBusiness)) {
                            setFieldValue('natureOfBusiness', '', false);
                          }
                        }}
                        fieldType="autocomplete"
                        automationIdPrefix="Onboarding_Create_Account_Your_Business_Field_Industry"
                        isDark={true}
                        className={
                          values.tradingNameDifferentToBusinessName || showCompanyRegNumber
                            ? ''
                            : 'top-field'
                        }
                      />
                      <Field
                        name="natureOfBusiness"
                        type="text"
                        placeholder="What is the nature of your business?"
                        component={MiniAppFocusWrapper}
                        innerComponent={FloatingInput}
                        disabled={!selectedIndustry}
                        items={selectedIndustry?.categories || []}
                        getItemDisplayValue={(item) => item.description}
                        filterItemsWithInputValue={true}
                        fieldType="autocomplete"
                        automationIdPrefix="Onboarding_Create_Account_Your_Business_Field_Nature_Of_Business"
                        isDark={true}
                        className="bottom-field"
                      />
                      {isSovRequest && (
                        <>
                          {isNotSoleProp(lead.typeOfBusiness) && (
                            <Field
                              name="occupation"
                              type="text"
                              placeholder="Occupation"
                              items={SOV_DETAILS.Occupations}
                              getItemDisplayValue={(item) => item}
                              component={MiniAppFocusWrapper}
                              innerComponent={FloatingInput}
                              fieldType="select"
                              isDark={true}
                              automationIdPrefix="Onboarding_Create_Account_Your_Details_Occupation"
                              // onItemSelect={(item) => TealiumEvents.bankingAccountType(item, customerType)}
                            />
                          )}
                          <Field
                            name="sourceOfFunds"
                            type="text"
                            placeholder="Source of funds"
                            items={SOV_DETAILS.SourceOfFunds}
                            // maxLength={BUSINESS_REFERENCE_MAX_LENGTH}
                            component={MiniAppFocusWrapper}
                            getItemDisplayValue={(item) => item}
                            innerComponent={FloatingInput}
                            fieldType="select"
                            isDark={true}
                            automationid="Onboarding_Create_Account_Your_Business_Field_SourceOfFunds"
                          />
                          <Field
                            name="sourceOfWealth"
                            type="text"
                            placeholder="Source of wealth"
                            items={SOV_DETAILS.SourceOfWealth}
                            // maxLength={BUSINESS_REFERENCE_MAX_LENGTH}
                            component={MiniAppFocusWrapper}
                            getItemDisplayValue={(item) => item}
                            innerComponent={FloatingInput}
                            fieldType="select"
                            isDark={true}
                            className="bottom-field"
                            automationid="Onboarding_Create_Account_Your_Business_Field_SourceOfWealth"
                          />
                        </>
                      )}
                      <ShowIf condition={isEcommerceSelected}>
                        <Field
                          name="ecommerceUrl"
                          type="text"
                          placeholder="What is your e-commerce / business url"
                          // maxLength={BUSINESS_REFERENCE_MAX_LENGTH}
                          component={MiniAppFocusWrapper}
                          innerComponent={FloatingInputVersionTwo}
                          fieldType="standard"
                          className="dark top-field"
                          automationid="Onboarding_Create_Account_Your_Business_Field_Ecommerce_Url"
                          icon={
                            <Info
                              text={businessUrlInfoText}
                              orientation="top"
                              className="input-icon-container"
                              onOpenEvent={() => TealiumEvents.generalInfoClick(customerType)}
                            />
                          }
                          {...(values.businessName ? { autoFocus: true } : {})}
                        />
                      </ShowIf>
                    </FormLayout>
                    <div className="mini-spacer" />
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );

  function businessRegistrationCheck(businessRegistration) {
    if (showCompanyRegNumber) {
      if (isTrust) {
        const invalidRegNum =
          !businessRegistration || !isValidRegistrationNumberTrust(businessRegistration);
        const message = invalidRegNum ? 'Invalid format, expected IT000000/0000' : null;
        return { isRegistrationValid: !invalidRegNum, validationMessage: message };
      } else {
        // reg number is optional for partnerships but if it is supplied, we validate it
        const invalidRegNum =
          !isValidRegistrationNumber(businessRegistration) &&
          (lead.typeOfBusiness !== 'Partnership' ||
            (businessRegistration && businessRegistration !== ''));
        const message = invalidRegNum ? 'Invalid format, expected 0000/000000/00' : null;
        return { isRegistrationValid: !invalidRegNum, validationMessage: message };
      }
    } else {
      return { isRegistrationValid: true, validationMessage: null };
    }
  }

  function isFormValid(values) {
    const invalidBusinessName =
      !values?.businessName || values.businessName.length > BUSINESS_REFERENCE_MAX_LENGTH;
    const invalidTradingAs =
      values.tradingNameDifferentToBusinessName &&
      (!values?.tradingAs || values.tradingAs.length > BUSINESS_REFERENCE_MAX_LENGTH);
    const invalidIndustry = !values?.industry || !getIndustry(values?.industry);
    const invalidNatureOfBusiness =
      !values?.natureOfBusiness || !getCategory(values?.industry, values?.natureOfBusiness);
    const invalidRegNum = !businessRegistrationCheck(values.companyRegNumber).isRegistrationValid;

    const invalidOccupation =
      isNotSoleProp(lead.typeOfBusiness) && merchant?.account?.sov_requested
        ? !values?.occupation
        : false;
    const invalidSourceOfFunds = merchant?.account?.sov_requested ? !values?.sourceOfFunds : false;
    const invalidSourceOfWealth = merchant?.account?.sov_requested
      ? !values?.sourceOfWealth
      : false;

    const invalidEcommerceUrl = isEcommerceSelected && !values?.ecommerceUrl;

    const isCreditCheckConsentOutstanding = isCreditVettingEnabled && !values.creditCheckConsent;
    const invalidBusinessRegistrationDate =
      isCreditVettingEnabled &&
      !values.businessRegistrationDate &&
      isNotSoleProp(lead.typeOfBusiness);

    return !(
      invalidBusinessName ||
      invalidTradingAs ||
      invalidIndustry ||
      invalidNatureOfBusiness ||
      invalidRegNum ||
      invalidOccupation ||
      invalidSourceOfFunds ||
      invalidSourceOfWealth ||
      isCreditCheckConsentOutstanding ||
      invalidBusinessRegistrationDate ||
      invalidEcommerceUrl
    );
  }

  function validateForm(values) {
    const errors = {};
    let newInputDanger = inputDanger;

    const invalidBusinessName =
      !values.businessName || values.businessName.length > BUSINESS_REFERENCE_MAX_LENGTH;
    newInputDanger['business-name'] = invalidBusinessName;
    errors.businessName = invalidBusinessName ? 'The business name entered is invalid' : null;

    if (isCreditVettingEnabled) {
      const invalidBusinessRegistrationDate = !values.businessRegistrationDate;
      newInputDanger['businessRegistrationDate'] = invalidBusinessRegistrationDate;
      errors.businessRegistrationDate = invalidBusinessRegistrationDate
        ? 'The business registration date entered is invalid'
        : null;
    }

    const invalidTradingAs =
      values.tradingNameDifferentToBusinessName &&
      (!values.tradingAs || values.tradingAs.length > BUSINESS_REFERENCE_MAX_LENGTH);
    newInputDanger['trading-as'] = invalidTradingAs;
    errors.tradingAs = invalidTradingAs ? 'The trading name entered is invalid' : null;

    const invalidIndustry = !values?.industry || !getIndustry(values?.industry);
    newInputDanger['industry'] = invalidIndustry;
    errors.industry = invalidIndustry ? 'The industry entered is invalid' : null;

    const invalidNatureOfBusiness =
      !values?.natureOfBusiness || !getCategory(values?.industry, values?.natureOfBusiness);
    newInputDanger['natureOfBusiness'] = invalidNatureOfBusiness;
    errors.natureOfBusiness = invalidNatureOfBusiness
      ? 'The nature of business entered is invalid'
      : null;

    errors.companyRegNumber = businessRegistrationCheck(values.companyRegNumber).validationMessage;

    const invalidBusinessUrl =
      isEcommerceSelected && (!values?.ecommerceUrl || !isValidUrl(values.ecommerceUrl));
    errors.ecommerceUrl = invalidBusinessUrl
      ? 'The website does not seem to be a valid e-commerce platform'
      : null;

    if (isCreditVettingEnabled) {
      const invalidBusinessRegistrationDate =
        !values?.businessRegistrationDate && isNotSoleProp(lead.typeOfBusiness);
      errors.businessRegistrationDate = invalidBusinessRegistrationDate
        ? 'The business registration date entered is invalid'
        : null;
    }

    const invalidOccupation =
      merchant?.account?.sov_requested && isNotSoleProp(lead.typeOfBusiness)
        ? !values?.occupation
        : false;
    newInputDanger['occupation'] = invalidOccupation;
    errors.occupation = invalidOccupation ? 'The occupation entered is invalid' : null;

    const invalidSourceOfFunds = merchant?.account?.sov_requested ? !values?.sourceOfFunds : false;
    newInputDanger['sourceOfFunds'] = invalidSourceOfFunds;
    errors.sourceOfFunds = invalidSourceOfFunds ? 'The source of funds entered is invalid' : null;

    const invalidSourceOfWealth = merchant?.account?.sov_requested
      ? !values?.sourceOfWealth
      : false;
    newInputDanger['sourceOfWealth'] = invalidSourceOfWealth;
    errors.sourceOfWealth = invalidSourceOfWealth
      ? 'The source of wealth entered is invalid'
      : null;

    setInputDanger(newInputDanger);

    let anyErrors = false;
    Object.keys(errors).forEach((key) => {
      if (errors[key] !== null) {
        anyErrors = true;
      }
    });

    if (!anyErrors) {
      setBusinessDetails(values);
      props.toggleNext(true);
    } else {
      props.toggleNext(false);
    }
    return errors;
  }
}
